import { useCallback, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { useAppDispatch } from '@/app/hooks';
import {
  checkTermsAndConditionsFromJWT,
  fetchLatestTermsAndConditions,
  recheckTermsAndConditions,
  resetTermsAndConditionsState,
  hasMandatoryUnacceptedTerms,
  JWTTermsAndConditionsClaims,
} from '@/features/termsAndConditions/termsAndConditionsSlice';
import { decodeToken } from '@/utils/decodeToken';

export const useTermsAndConditionsCheck = () => {
  const dispatch = useAppDispatch();
  const effectiveDateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearEffectiveDateTimer = useCallback(() => {
    if (effectiveDateTimer.current) {
      clearTimeout(effectiveDateTimer.current);
      effectiveDateTimer.current = null;
    }
  }, []);

  const scheduleEffectiveDateRecheck = useCallback(
    (effectiveDate: string | null) => {
      clearEffectiveDateTimer();
      if (!effectiveDate) return;

      const effectiveTimestamp = Date.parse(effectiveDate);
      if (!Number.isFinite(effectiveTimestamp) || effectiveTimestamp <= Date.now()) return;

      const scheduleNextCheck = () => {
        const remainingTime = effectiveTimestamp - Date.now();
        effectiveDateTimer.current = setTimeout(
          () => {
            if (Date.now() < effectiveTimestamp) {
              scheduleNextCheck();
            } else if (Cookies.get('accessToken')) {
              dispatch(recheckTermsAndConditions());
            } else {
              dispatch(resetTermsAndConditionsState());
            }
          },
          Math.min(remainingTime, 2_147_000_000)
        );
      };

      scheduleNextCheck();
    },
    [clearEffectiveDateTimer, dispatch]
  );

  const extractTermsAndConditionsClaimsFromJWT = useCallback((): JWTTermsAndConditionsClaims => {
    const accessToken = Cookies.get('accessToken');

    if (!accessToken) {
      return { acceptedTermsId: null, latestTerms: null };
    }

    try {
      const decoded = decodeToken(accessToken);
      return {
        acceptedTermsId: decoded.acceptedTermsId || null,
        latestTerms: decoded.latestTerms || null,
      };
    } catch (error) {
      console.error('Failed to decode JWT for Terms and Conditions claims:', error);
      return { acceptedTermsId: null, latestTerms: null };
    }
  }, []);

  const checkTermsAndConditions = useCallback((): boolean => {
    const claims = extractTermsAndConditionsClaimsFromJWT();

    dispatch(checkTermsAndConditionsFromJWT(claims));

    scheduleEffectiveDateRecheck(claims.latestTerms?.effectiveDate ?? null);
    // The JWT claim is only a hint: it can be missing or stale (BE gateway#15).
    // The published endpoint is the source of truth for the latest version,
    // its content and its effective date, so it is always queried.
    dispatch(fetchLatestTermsAndConditions());
    return hasMandatoryUnacceptedTerms(claims);
  }, [dispatch, extractTermsAndConditionsClaimsFromJWT, scheduleEffectiveDateRecheck]);

  const recheckTermsAndConditionsStatus = useCallback(() => {
    const claims = extractTermsAndConditionsClaimsFromJWT();
    dispatch(checkTermsAndConditionsFromJWT(claims));
    scheduleEffectiveDateRecheck(claims.latestTerms?.effectiveDate ?? null);
  }, [dispatch, extractTermsAndConditionsClaimsFromJWT, scheduleEffectiveDateRecheck]);

  const triggerRecheck = useCallback(() => {
    dispatch(recheckTermsAndConditions());
  }, [dispatch]);

  useEffect(() => clearEffectiveDateTimer, [clearEffectiveDateTimer]);

  return {
    checkTermsAndConditions,
    recheckTermsAndConditionsStatus,
    triggerRecheck,
    extractTermsAndConditionsClaimsFromJWT,
  };
};

export default useTermsAndConditionsCheck;
